/**
 * /pqrsf/:ticketId — Detalle del trámite + trazabilidad.
 *
 * Es la pantalla que un funcionario DIAN abriría en la visita de
 * verificación (Res. 227/2025 art. 1.5.1.8.1.1 num. 10): muestra la
 * solicitud, la respuesta y la línea de tiempo completa de eventos, con
 * el resultado de verificar la cadena de hashes de la trazabilidad.
 */

import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  FileText,
  Lock,
  ShieldAlert,
  ShieldCheck,
  StickyNote,
} from 'lucide-react'

import {
  usePqrsfAddNote,
  usePqrsfChangeStatus,
  usePqrsfDetail,
  usePqrsfRespond,
} from '@/hooks/api/use-pqrsf'
import {
  PQRSF_ALLOWED_TRANSITIONS,
  PQRSF_EVENT_LABELS,
  PQRSF_KIND_LABELS,
  PQRSF_STATUS_LABELS,
  type PqrsfEvent,
  type PqrsfStatus,
  type PqrsfTicket,
} from '@/lib/api'
import { ROUTES } from '@/lib/constants'
import { formatDate } from './index'

export function PqrsfDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>()
  const detail = usePqrsfDetail(ticketId)

  if (detail.isLoading) {
    return <p className="text-sm text-fg-muted">Cargando trámite…</p>
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="mx-auto max-w-3xl">
        <BackLink />
        <p className="mt-4 text-sm text-error-fg">
          No se pudo cargar el trámite. Puede que el radicado ya no exista.
        </p>
      </div>
    )
  }

  const { ticket, timeline, chain_valid: chainValid } = detail.data

  return (
    <div className="mx-auto max-w-6xl">
      <BackLink />

      {/* Encabezado */}
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-sm font-semibold text-brand-700">{ticket.radicado}</p>
          <h1 className="mt-1 text-2xl font-bold text-fg-primary">{ticket.subject}</h1>
          <p className="mt-1 text-sm text-fg-secondary">
            {PQRSF_KIND_LABELS[ticket.kind]} · radicada el {formatDate(ticket.filed_at)}
            {ticket.due_at && ` · vence el ${formatDate(ticket.due_at)}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-bg-elevated px-3 py-1 text-sm font-medium text-fg-primary">
            {PQRSF_STATUS_LABELS[ticket.status]}
          </span>
          <ChainBadge valid={chainValid} />
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Columna izquierda: contenido + acciones */}
        <div className="space-y-6 lg:col-span-2">
          <Card title="Solicitud" icon={FileText}>
            <p className="whitespace-pre-wrap text-sm text-fg-primary">{ticket.description}</p>
          </Card>

          {ticket.response_text && (
            <Card title="Respuesta" icon={CheckCircle2}>
              <p className="whitespace-pre-wrap text-sm text-fg-primary">
                {ticket.response_text}
              </p>
              <p className="mt-3 text-xs text-fg-muted">
                Respondida el {ticket.responded_at ? formatDate(ticket.responded_at) : '—'}
                {ticket.responded_by_email && ` por ${ticket.responded_by_email}`}
              </p>
            </Card>
          )}

          {ticketId && <Actions ticket={ticket} ticketId={ticketId} />}
        </div>

        {/* Columna derecha: solicitante + trazabilidad */}
        <div className="space-y-6">
          <Card title="Solicitante">
            <Field label="Nombre" value={ticket.requester_name} />
            <Field
              label="Documento"
              value={`${ticket.requester_doc_type ? `${ticket.requester_doc_type} · ` : ''}${ticket.requester_doc_number}`}
            />
            <Field label="Correo" value={ticket.requester_email} />
            {ticket.requester_phone && <Field label="Teléfono" value={ticket.requester_phone} />}
            <Field label="Canal" value={ticket.source_channel} />
            <Field
              label="Tratamiento de datos"
              value={ticket.privacy_accepted ? 'Aceptado (Ley 1581/2012)' : 'No aceptado'}
            />
          </Card>

          <Timeline events={timeline} />
        </div>
      </div>
    </div>
  )
}

function BackLink() {
  return (
    <Link
      to={ROUTES.PQRSF}
      className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg-primary"
    >
      <ArrowLeft className="h-4 w-4" />
      Volver a la bandeja
    </Link>
  )
}

function ChainBadge({ valid }: { valid: boolean }) {
  if (valid) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-success-tint px-3 py-1 text-xs font-medium text-success-fg"
        title="La cadena de hashes de la trazabilidad se recalculó y coincide: ningún asiento fue alterado."
      >
        <ShieldCheck className="h-3.5 w-3.5" />
        Trazabilidad íntegra
      </span>
    )
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-error-tint px-3 py-1 text-xs font-medium text-error-fg"
      title="La cadena de hashes no coincide. Escalar a seguridad: la trazabilidad pudo ser alterada."
    >
      <ShieldAlert className="h-3.5 w-3.5" />
      Cadena inconsistente
    </span>
  )
}

function Actions({ ticket, ticketId }: { ticket: PqrsfTicket; ticketId: string }) {
  const [responseText, setResponseText] = useState('')
  const [closeOnRespond, setCloseOnRespond] = useState(false)
  const [note, setNote] = useState('')
  const [noteIsPublic, setNoteIsPublic] = useState(false)
  const [statusComment, setStatusComment] = useState('')

  const respond = usePqrsfRespond(ticketId)
  const changeStatus = usePqrsfChangeStatus(ticketId)
  const addNote = usePqrsfAddNote(ticketId)

  const transitions = PQRSF_ALLOWED_TRANSITIONS[ticket.status]
  const canRespond =
    transitions.includes('resuelto') || transitions.includes('cerrado')

  return (
    <div className="space-y-6">
      {canRespond ? (
        <Card title="Responder de fondo">
          <textarea
            rows={5}
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            placeholder="Respuesta que se mostrará al solicitante cuando consulte su radicado…"
            className="w-full rounded-md border border-border bg-bg-app px-3 py-2 text-sm text-fg-primary"
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm text-fg-secondary">
              <input
                type="checkbox"
                checked={closeOnRespond}
                onChange={(e) => setCloseOnRespond(e.target.checked)}
              />
              Cerrar el trámite al responder
            </label>
            <button
              type="button"
              disabled={responseText.trim().length < 10 || respond.isPending}
              onClick={() =>
                respond.mutate(
                  {
                    response_text: responseText.trim(),
                    status: closeOnRespond ? 'cerrado' : 'resuelto',
                  },
                  { onSuccess: () => setResponseText('') },
                )
              }
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {respond.isPending ? 'Registrando…' : 'Registrar respuesta'}
            </button>
          </div>
        </Card>
      ) : (
        <Card title="Trámite cerrado" icon={Lock}>
          <p className="text-sm text-fg-secondary">
            Un trámite cerrado no admite más cambios de estado ni respuestas. La
            trazabilidad queda como registro definitivo; si el solicitante insiste,
            debe radicarse una nueva solicitud.
          </p>
        </Card>
      )}

      {transitions.length > 0 && (
        <Card title="Cambiar estado">
          <input
            type="text"
            value={statusComment}
            onChange={(e) => setStatusComment(e.target.value)}
            placeholder="Motivo del cambio (queda en la trazabilidad)"
            className="w-full rounded-md border border-border bg-bg-app px-3 py-2 text-sm text-fg-primary"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {transitions.map((target: PqrsfStatus) => (
              <button
                key={target}
                type="button"
                disabled={changeStatus.isPending}
                onClick={() =>
                  changeStatus.mutate(
                    { status: target, comment: statusComment.trim() || undefined },
                    { onSuccess: () => setStatusComment('') },
                  )
                }
                className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-60"
              >
                Mover a {PQRSF_STATUS_LABELS[target]}
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card title="Agregar nota" icon={StickyNote}>
        <textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Nota de gestión…"
          className="w-full rounded-md border border-border bg-bg-app px-3 py-2 text-sm text-fg-primary"
        />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm text-fg-secondary">
            <input
              type="checkbox"
              checked={noteIsPublic}
              onChange={(e) => setNoteIsPublic(e.target.checked)}
            />
            Visible para el solicitante en la consulta pública
          </label>
          <button
            type="button"
            disabled={note.trim() === '' || addNote.isPending}
            onClick={() =>
              addNote.mutate(
                { comment: note.trim(), is_public: noteIsPublic },
                { onSuccess: () => setNote('') },
              )
            }
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            {addNote.isPending ? 'Guardando…' : 'Agregar nota'}
          </button>
        </div>
      </Card>
    </div>
  )
}

function Timeline({ events }: { events: PqrsfEvent[] }) {
  return (
    <Card title="Trazabilidad">
      <p className="mb-4 text-xs text-fg-muted">
        Registro append-only: los asientos no se editan ni se borran, y cada uno
        encadena el hash del anterior.
      </p>
      <ol className="space-y-4">
        {events.map((ev) => (
          <li key={ev.id} className="border-l-2 border-border pl-4">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-fg-muted">#{ev.seq}</span>
              <span className="text-sm font-medium text-fg-primary">
                {PQRSF_EVENT_LABELS[ev.event_type]}
              </span>
              {!ev.is_public && (
                <span className="rounded bg-bg-elevated px-1.5 py-0.5 text-[11px] text-fg-muted">
                  interna
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-fg-muted">
              {formatDate(ev.created_at)}
              {ev.actor_email ? ` · ${ev.actor_email}` : ` · ${ev.actor_kind}`}
            </p>
            {ev.from_status && ev.to_status && ev.from_status !== ev.to_status && (
              <p className="mt-1 text-xs text-fg-secondary">
                {ev.from_status} → {ev.to_status}
              </p>
            )}
            {ev.comment && (
              <p className="mt-1 whitespace-pre-wrap text-sm text-fg-secondary">{ev.comment}</p>
            )}
            <p
              className="mt-1 truncate font-mono text-[11px] text-fg-muted"
              title={`hash: ${ev.this_hash}`}
            >
              {ev.this_hash.slice(0, 16)}…
            </p>
          </li>
        ))}
      </ol>
    </Card>
  )
}

function Card({
  title,
  icon: Icon,
  children,
}: {
  title: string
  icon?: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}) {
  return (
    <section className="rounded-xl border border-border bg-bg-surface p-5 shadow-sm">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-fg-primary">
        {Icon && <Icon className="h-4 w-4 text-fg-muted" />}
        {title}
      </h2>
      {children}
    </section>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="mb-2">
      <p className="font-mono text-[11px] uppercase tracking-wider text-fg-muted">{label}</p>
      <p className="text-sm text-fg-primary">{value}</p>
    </div>
  )
}
